export type LegalSection = {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  note?: string;
};

export type LegalDocument = {
  path: string;
  title: string;
  shortTitle: string;
  summary: string;
  status: "Effective";
  effectiveDate: string;
  highlights?: { label: string; value: string }[];
  sections: LegalSection[];
};

const updated = "31 August 2026";

export const legalDocuments: LegalDocument[] = [
  {
    path: "/privacy",
    title: "Privacy notice",
    shortTitle: "Privacy",
    summary:
      "How Envelaro collects, uses, shares, and protects personal data across its website, mobile app, portal, and managed services.",
    status: "Effective",
    effectiveDate: "9 September 2026",
    highlights: [
      { label: "Applies to", value: "Website, Envelaro Today, portal, and managed services" },
      { label: "Data sale", value: "We do not sell personal information" },
      { label: "Your control", value: "Access, correct, delete, or object" },
    ],
    sections: [
      {
        id: "scope",
        title: "1. Scope and who we are",
        paragraphs: [
          "This notice applies to Envelaro’s public website, Envelaro Today mobile app, account registration, contact form, customer portal, managed email, and related communications. Customer mailbox content and other data processed on a customer’s instructions are also governed by the Service Terms and Data Processing Addendum.",
          "Envelaro is the controller of account, billing, website, and business-contact data used to operate the service. For customer content, Envelaro generally acts as the customer’s processor or service provider. Privacy requests can be submitted through our contact page.",
        ],
      },
      {
        id: "collection",
        title: "2. Information we collect",
        bullets: [
          "Information you submit: name, work email, company, team size, current email provider, and the content of your enquiry. Campaign labels (source, medium, campaign, content and term) and the landing-page path may accompany an enquiry so we can understand how you found us. They are retained with the enquiry; they are held only in memory during public-site navigation, not in persistent advertising storage.",
          "Account and organisation data: administrator and user profiles, roles, verified domains, mailbox addresses, configuration, entitlements, and consent records.",
          "Customer content: messages, attachments, contacts, calendars, tasks, files, and related metadata processed to provide customer-enabled features.",
          "Billing data: plan, order, tax location, invoice, subscription, payment status, and processor references. Envelaro does not store full payment-card numbers in its application databases.",
          "Integration data: provider identifiers, authorisations, configuration, and activity needed for a customer-selected DNS, storage, identity, or communication integration.",
          "Consent records: the notice version accepted, submission time, and related confirmation data.",
          "Technical and security data: IP address, browser and device information, request timestamps, error records, and signals used to prevent spam, abuse, or automated submissions.",
          "Correspondence: messages and follow-up communications you exchange with us.",
          "Mobile app data: mailbox addresses and settings, message content and metadata, attachments, contact details selected from the device, notification data, and diagnostic or security information needed to connect, synchronise, send, receive, and protect email.",
        ],
      },
      {
        id: "use",
        title: "3. How and why we use information",
        bullets: [
          "To respond to enquiries and discuss Envelaro services.",
          "To create accounts, verify organisations and domains, provision requested features, process subscriptions, and provide customer support.",
          "To route and store customer communications and content according to authorised user actions and customer configuration.",
          "To let Envelaro Today connect to the user’s selected mail service, synchronise mail, send user-directed messages and attachments, display notifications, and provide optional contact and biometric features.",
          "To send service and product communications when you have asked us to contact you.",
          "To understand customer needs and improve our services.",
          "To secure the website, prevent abuse, diagnose faults, and maintain audit records.",
          "To comply with applicable legal obligations and establish, exercise, or defend legal claims.",
        ],
        note:
          "Depending on applicable law, these activities rely on consent, steps requested before entering a contract, legitimate interests in operating and securing the website, or legal obligations. You may withdraw consent at any time.",
      },
      {
        id: "sharing",
        title: "4. Sharing and service providers",
        paragraphs: [
          "We do not sell personal information. If Meta advertising measurement is configured and you allow it, page visits and successful enquiry events are shared with Meta, which may associate them with your Meta account and use them for advertising measurement and personalisation. You can reject or withdraw this optional sharing through Advertising privacy choices. Otherwise, we disclose information to personnel who need it, service providers acting on our instructions, professional advisers, authorities when legally required, or a successor in a legitimate corporate transaction. See our cookie notice for details.",
          "The current public website uses Google Cloud and Firebase for hosting, form processing, database storage, security, and application-integrity checks. The current list is maintained on our Subprocessors page.",
          "Envelaro Today connects to the mail servers configured by the user. Messages and attachments are transmitted to those providers and to recipients chosen by the user. The Android app does not contain advertising SDKs and its Envelaro release configuration disables optional product telemetry.",
        ],
      },
      {
        id: "transfers",
        title: "5. International transfers",
        paragraphs: [
          "Our providers may process information in countries other than your own. Where applicable law requires it, we use recognised transfer mechanisms and contractual safeguards, and assess the protections available for the transfer.",
        ],
      },
      {
        id: "retention",
        title: "6. Retention",
        paragraphs: [
          "We keep enquiry and account data only while it remains useful for the purpose described above, while we have an active relationship with you, or as needed for legal, billing, fraud-prevention, and security obligations. We periodically review records and delete or anonymise information that is no longer required.",
          "The Data Lifecycle and Export Policy describes customer-content export, active-system deletion, and backup expiry after service ends. A signed order or applicable law may require a different period.",
          "You may ask us to delete your enquiry record at any time. Some limited information may be retained where required for security, legal compliance, dispute resolution, or to record an opt-out.",
          "Removing a mailbox from Envelaro Today removes the app’s local account configuration and locally cached content from that device. It does not by itself delete the mailbox or customer account held by the selected provider. Envelaro service deletion and export requests follow the Service Terms and Data Lifecycle and Export Policy.",
        ],
      },
      {
        id: "rights",
        title: "7. Your choices and rights",
        bullets: [
          "Ask whether we process your personal data and request a copy.",
          "Request correction, deletion, restriction, or portability where applicable.",
          "Object to certain processing or withdraw consent without affecting earlier lawful processing.",
          "Unsubscribe from product communications or ask us not to contact you.",
          "Complain to the data-protection authority available in your jurisdiction.",
        ],
        note:
          "We may need to verify your identity before completing a request. Applicable rights and exceptions depend on your location.",
      },
      {
        id: "children",
        title: "8. Children",
        paragraphs: [
          "The website, contact form, portal, managed services, and Envelaro Today are intended for business users aged 18 or older. We do not knowingly collect personal data from children.",
        ],
      },
      {
        id: "security",
        title: "9. Security and changes",
        paragraphs: [
          "We use administrative, technical, and organisational measures proportionate to the website’s current risk. No online system is completely secure, and we cannot guarantee absolute security.",
          "We may update this notice as the product, providers, or applicable requirements change. The date shown above identifies the current version. Material changes will be highlighted on this page or communicated where appropriate.",
        ],
      },
      {
        id: "information-sources",
        title: "10. Information sources and sensitive data",
        paragraphs: [
          "We receive most personal data directly from you. We may also use public company websites, professional directories, or information supplied by a colleague when verifying an organisation, preparing a response, or preventing fraud.",
          "The public website is not designed to collect special-category or highly sensitive personal data. Please do not submit passwords, mailbox contents, payment-card details, government identifiers, health information, or similar data unless we have specifically requested it through an appropriate secure channel.",
        ],
      },
      {
        id: "communications",
        title: "11. Communications and marketing",
        paragraphs: [
          "We may send messages needed to respond to an enquiry or manage a requested relationship. We send optional product news or marketing only where permitted by law and provide a clear way to opt out.",
          "Opting out of marketing does not prevent us from replying to an active request or sending necessary service, security, legal, or administrative notices. We may retain a minimal suppression record so we can respect your choice.",
        ],
      },
      {
        id: "automated-decisions",
        title: "12. Automated decisions",
        paragraphs: [
          "We do not use information collected through the public website to make decisions that produce legal or similarly significant effects solely by automated means.",
          "Security services may automatically assess request, browser, device, and network signals to identify spam or abuse. A blocked form submission does not determine eligibility for a paid Envelaro service; you may contact us through another published channel if you believe a security control acted incorrectly.",
        ],
      },
      {
        id: "requests",
        title: "13. How we handle privacy requests",
        paragraphs: [
          "Submit a request through our contact page and describe the right you want to exercise. We will acknowledge the request, verify identity where reasonably necessary, and respond within the period required by applicable law.",
          "If we cannot fulfil all or part of a request, we will explain the reason where the law permits. Authorised agents may submit a request on another person's behalf, but we may ask for evidence of that authority and direct identity confirmation.",
        ],
        note:
          "Privacy requests are handled without charge unless a request is manifestly unfounded, excessive, or repetitive and applicable law allows a reasonable fee or refusal.",
      },
    ],
  },
  {
    path: "/terms",
    title: "Website terms of use",
    shortTitle: "Terms",
    summary:
      "The terms governing use of Envelaro’s public website, resources, and contact form.",
    status: "Effective",
    effectiveDate: updated,
    highlights: [
      { label: "Applies to", value: "The public website and enquiry forms" },
      { label: "Paid services", value: "Covered by separate written terms" },
      { label: "Minimum age", value: "18 years old" },
    ],
    sections: [
      {
        id: "agreement",
        title: "1. Agreement and scope",
        paragraphs: [
          "By using this website, you agree to these terms. They govern the public website and contact form only. They do not create an email-hosting subscription, service-level commitment, or reseller relationship.",
          "Paid subscriptions are governed by the Service Terms, Billing, Cancellation and Refund Policy, Acceptable Use Policy, Data Processing Addendum, the plan selected at checkout, and any signed order that expressly modifies them.",
        ],
      },
      {
        id: "eligibility",
        title: "2. Eligibility and accurate information",
        paragraphs: [
          "You must be at least 18 and able to enter a binding agreement. If you use the website for an organisation, you confirm that you are authorised to act for it.",
          "Information submitted through the contact form must be accurate, current, and your own to provide. You must not impersonate another person or submit sensitive, confidential, or regulated information that we have not requested.",
        ],
      },
      {
        id: "use",
        title: "3. Permitted use",
        bullets: [
          "Use the website for legitimate evaluation, enquiry, and business purposes.",
          "Do not interfere with the website, probe it without authorisation, bypass safeguards, introduce malicious code, scrape it excessively, or use it to violate another person’s rights.",
          "Do not misrepresent Envelaro, copy the website to deceive others, or use our branding without permission.",
        ],
      },
      {
        id: "website-information",
        title: "4. Website information",
        paragraphs: [
          "Product descriptions, screenshots, availability statements, storage amounts, migration options, and pricing describe the service at the date shown and may change.",
          "Website content does not replace the written order, service agreement, or other terms accepted by a customer.",
        ],
      },
      {
        id: "ip",
        title: "5. Intellectual property and feedback",
        paragraphs: [
          "The website, branding, original copy, interface, and related materials are owned by or licensed to Envelaro and are protected by applicable intellectual-property laws.",
          "If you voluntarily provide product feedback, you allow us to use it without restriction or compensation, provided we do not publicly identify you without permission.",
        ],
      },
      {
        id: "third-party",
        title: "6. Third-party services and links",
        paragraphs: [
          "Links and integrations may lead to services operated by others. Their terms and privacy practices apply independently, and we are not responsible for their content or availability.",
        ],
      },
      {
        id: "disclaimer",
        title: "7. Disclaimers",
        paragraphs: [
          "The public website is provided on an “as is” and “as available” basis. To the extent permitted by law, we disclaim implied warranties relating to availability, accuracy, fitness for a particular purpose, and non-infringement.",
          "Nothing in these terms excludes rights or liabilities that cannot lawfully be excluded, including liability for fraud or other non-excludable harm.",
        ],
      },
      {
        id: "liability",
        title: "8. Liability",
        paragraphs: [
          "To the extent permitted by law, Envelaro is not liable for indirect, incidental, special, consequential, or lost-profit damages arising from use of this public website. These limits do not override mandatory consumer protections.",
        ],
      },
      {
        id: "changes",
        title: "9. Suspension, changes, and contact",
        paragraphs: [
          "We may change, suspend, or withdraw any part of the website or contact form. We may also restrict access that threatens security, violates these terms, or creates legal or operational risk.",
          "Updated terms apply from the date shown above. Questions may be submitted through the contact page.",
        ],
      },
      {
        id: "enquiries",
        title: "10. Enquiries, demos, and proposals",
        paragraphs: [
          "Submitting an enquiry does not require either party to proceed with a transaction. Any estimate, roadmap statement, demonstration, or preliminary proposal remains subject to technical discovery, availability, written scope, and final commercial approval.",
          "You are responsible for ensuring that you may share the business information included in an enquiry. We may decline or stop a discussion where a request is unlawful, misleading, abusive, outside our capabilities, or creates unacceptable security or operational risk.",
        ],
      },
      {
        id: "privacy-security",
        title: "11. Privacy and security",
        paragraphs: [
          "Our Privacy Notice explains how we handle personal data submitted through the website. You are responsible for using appropriate security when accessing the site and for avoiding the transmission of credentials or unnecessary sensitive information through public forms.",
          "If you discover a suspected vulnerability, misuse, or impersonation involving Envelaro, report it through the contact page. Do not exploit, publicly disclose, or retain information beyond what is reasonably necessary to document the issue.",
        ],
      },
      {
        id: "responsibility",
        title: "12. Responsibility for misuse",
        paragraphs: [
          "To the extent permitted by applicable law, you are responsible for losses, claims, and reasonable costs caused by your unlawful use of the website, deliberate violation of these terms, infringement of another person's rights, or submission of material you were not authorised to provide.",
          "This section does not apply to the extent a loss was caused by Envelaro or where the responsibility cannot lawfully be imposed.",
        ],
      },
      {
        id: "general",
        title: "13. General terms",
        bullets: [
          "If a provision is found unenforceable, the remaining provisions continue to apply to the fullest extent permitted by law.",
          "A delay in enforcing a right does not waive that right.",
          "You may not transfer your rights under these website terms without our written consent. We may transfer ours as part of a reorganisation, financing, or sale of the relevant business.",
          "These website terms and the policies they reference form the entire agreement about use of the public website. Separate signed service documents control where they conflict on paid services.",
        ],
        note:
          "Paid services are governed by the Service Terms and the order accepted at checkout, not these website-only terms.",
      },
    ],
  },
  {
    path: "/acceptable-use",
    title: "Acceptable use policy",
    shortTitle: "Acceptable use",
    summary:
      "Rules designed to protect users, recipients, infrastructure, and sending reputation from abuse.",
    status: "Effective",
    effectiveDate: updated,
    sections: [
      {
        id: "scope",
        title: "1. Scope",
        paragraphs: [
          "This policy applies to the Envelaro website, managed email, administration, APIs, communication features, storage, and related services. It supplements the Service Terms and applies during trials as well as paid subscriptions.",
        ],
      },
      {
        id: "prohibited",
        title: "2. Prohibited activity",
        bullets: [
          "Spam, unsolicited bulk email, purchased or harvested recipient lists, address enumeration, or deceptive list acquisition.",
          "Phishing, credential theft, malware, ransomware, malicious attachments, exploit delivery, or command-and-control activity.",
          "Fraud, impersonation, forged headers, misleading sender identity, domain abuse, or infringement of intellectual-property or privacy rights.",
          "Harassment, threats, exploitation, unlawful discrimination, or content that is illegal in an applicable jurisdiction.",
          "Operating an open relay or proxy, unauthorised scanning, credential stuffing, denial-of-service activity, or attempts to bypass authentication, quotas, or rate limits.",
          "Using compromised accounts or infrastructure, concealing abuse, or helping another person evade an enforcement action.",
        ],
      },
      {
        id: "sending",
        title: "3. Responsible sending",
        bullets: [
          "Send commercial or bulk messages only to recipients with a valid legal basis and the permission required by applicable law.",
          "Use accurate sender information, a monitored reply address, and a clear, functional unsubscribe method where required.",
          "Honour opt-outs promptly, maintain suppression records, and do not re-add recipients without valid renewed permission.",
          "Maintain SPF, DKIM, DMARC, bounce handling, complaint processing, and reasonable sending controls.",
        ],
      },
      {
        id: "security",
        title: "4. Account and domain security",
        paragraphs: [
          "Customers are responsible for authorised users, secure credentials, accurate domain configuration, and timely reporting of suspected compromise. Accounts must not be shared outside the licensed organisation or used to provide unauthorised downstream service.",
        ],
      },
      {
        id: "resources",
        title: "5. Fair use of resources",
        paragraphs: [
          "You must not use shared resources in a way that materially degrades service for others. Published storage, message-size, connection, recipient, and sending limits must be followed. Attempts to evade technical limits are prohibited.",
        ],
      },
      {
        id: "enforcement",
        title: "6. Investigation and enforcement",
        paragraphs: [
          "We may investigate credible reports, preserve relevant records, rate-limit sending, quarantine content, suspend access, or terminate service when reasonably necessary to protect recipients, customers, infrastructure, or legal compliance.",
          "Where circumstances permit, we will consider severity, intent, history, remediation, and immediate risk before acting. Urgent threats may require action without advance notice.",
        ],
      },
      {
        id: "report",
        title: "7. Reporting abuse",
        paragraphs: [
          "Report suspected abuse through the contact page with the relevant domain, sender, timestamps, message headers, and a concise description. Do not include passwords or unnecessary sensitive content.",
        ],
      },
    ],
  },
  {
    path: "/cookies",
    title: "Cookie notice",
    shortTitle: "Cookies",
    summary:
      "What browser storage and similar technologies the Envelaro website currently uses.",
    status: "Effective",
    effectiveDate: "9 September 2026",
    sections: [
      {
        id: "current-use",
        title: "1. Current use",
        paragraphs: [
          "Optional Meta advertising measurement is disabled unless a Meta Pixel is configured and you choose Allow advertising. When enabled with your permission, Meta receives page visits and successful enquiry events, along with browser, connection and cookie information. We do not intentionally send form contents, email addresses, mailbox data or payment details in those events. Meta may associate events with your Meta account and process them under its own privacy policy (https://www.facebook.com/privacy/policy/).",
          "We use a session-scoped browser-storage flag to count one anonymous visit per browser tab session. Only daily aggregate totals are retained; Envelaro does not store the visitor’s IP address, page history, or a persistent visitor identifier in the analytics record.",
          "The website may use strictly necessary storage and security signals to deliver requested functionality, protect forms, balance traffic, prevent abuse, and maintain a secure session.",
        ],
      },
      {
        id: "providers",
        title: "2. Firebase and application integrity",
        paragraphs: [
          "When the launch-list form is enabled, Google Firebase and reCAPTCHA Enterprise or App Check may use cookies, local storage, device signals, and request information to verify application integrity and distinguish legitimate submissions from abuse.",
          "These technologies are activated only for security and requested form functionality. Google’s own privacy terms govern its independent processing.",
        ],
      },
      {
        id: "consent",
        title: "3. Consent and future changes",
        paragraphs: [
          "You can accept or reject optional Meta measurement. Neither the Meta script nor its events load before acceptance. Where measurement is configured, Advertising privacy choices lets you change your choice at any time. Rejection stops future events and clears the Meta cookies this website can access; it does not undo earlier processing by Meta. We store your choice in local browser storage until you change it or clear site data. Meta may set _fbp and _fbc cookies, typically lasting up to 90 days.",
        ],
      },
      {
        id: "controls",
        title: "4. Your controls",
        paragraphs: [
          "Most browsers let you block or delete cookies and site storage. Blocking strictly necessary technologies may prevent the launch-list form or security controls from working correctly. Browser help documentation explains the controls available for your device.",
        ],
      },
    ],
  },
  {
    path: "/dpa",
    title: "Data processing addendum",
    shortTitle: "Data processing",
    summary:
      "The terms governing Envelaro’s processing of customer personal data for the managed service.",
    status: "Effective",
    effectiveDate: updated,
    sections: [
      {
        id: "status",
        title: "1. Application",
        paragraphs: [
          "This Data Processing Addendum forms part of the Service Terms when Envelaro processes personal data on a customer’s behalf. It applies from the date the customer accepts the Service Terms or signs an order that incorporates it.",
          "If a signed order contains a specifically negotiated data-processing term that conflicts with this addendum, that signed term controls for the conflict.",
        ],
      },
      {
        id: "roles",
        title: "2. Roles and instructions",
        paragraphs: [
          "For customer mailbox, account, and administrative data processed to provide the service, the customer will generally act as controller or business and Envelaro as processor or service provider. Envelaro will process that data only on documented instructions, including those inherent in providing the contracted service.",
        ],
      },
      {
        id: "details",
        title: "3. Processing details",
        bullets: [
          "Subject matter: hosted email, mailbox administration, security, support, migration, backup, and recovery.",
          "Data subjects: customer users, administrators, correspondents, contacts, and support participants.",
          "Data types: account identifiers, configuration, message content and metadata, contacts, logs, authentication records, and support data.",
          "Duration: the service term plus documented retention, deletion, backup, security, and legal-compliance periods.",
        ],
      },
      {
        id: "duties",
        title: "4. Processor commitments",
        bullets: [
          "Confidentiality obligations for authorised personnel.",
          "Risk-appropriate technical and organisational security measures.",
          "Reasonable assistance with data-subject requests, impact assessments, and regulatory consultations.",
          "Notice of confirmed personal-data breaches without undue delay as required by the final agreement.",
          "Deletion or return of customer personal data after termination, subject to documented legal and backup exceptions.",
        ],
      },
      {
        id: "subprocessors",
        title: "5. Subprocessors",
        paragraphs: [
          "The customer authorises the subprocessors on Envelaro’s current public list. Envelaro requires subprocessors to protect personal data consistently with their role and provides notice and a reasonable objection process where applicable law requires it.",
        ],
      },
      {
        id: "transfers",
        title: "6. International transfers",
        paragraphs: [
          "Where restricted transfers occur, the parties will use an applicable lawful transfer mechanism, which may include approved standard contractual clauses and supplementary measures.",
        ],
      },
      {
        id: "audit",
        title: "7. Information and audit",
        paragraphs: [
          "Envelaro will provide reasonable compliance information and, where legally required, support a proportionate audit process that protects other customers, security, confidentiality, and operational continuity. Independent reports and written responses will be used before an on-site audit where they adequately address the request.",
        ],
      },
    ],
  },
  {
    path: "/subprocessors",
    title: "Subprocessors",
    shortTitle: "Subprocessors",
    summary:
      "Third parties that may process customer data to operate Envelaro and customer-selected features.",
    status: "Effective",
    effectiveDate: updated,
    sections: [
      {
        id: "current",
        title: "1. Core service providers",
        bullets: [
          "Google Cloud Platform / Firebase — application hosting, authentication, serverless processing, database and file storage, application integrity, abuse prevention, secrets, and operational logging. Processing location depends on the configured service and Google’s infrastructure.",
          "Contabo — infrastructure hosting for Envelaro-operated mail and collaboration services. Open-source Mailu and related software run under Envelaro’s administration; customers do not receive server-level access.",
          "MailerSend — transactional account, verification, support, and service-notification email where that delivery route is used.",
        ],
      },
      {
        id: "feature-specific",
        title: "2. Feature-specific providers",
        bullets: [
          "Cloudflare — customer-authorised DNS automation, real-time communication connectivity, optional R2 storage, and AI assistance where the relevant feature is enabled.",
          "OpenAI — AI-assisted tenant administration or content features only when the customer invokes or enables the relevant feature.",
          "Stripe — checkout, subscription billing, payment records, and fraud prevention when Stripe checkout is offered for the customer’s region and order.",
        ],
        note:
          "Customer-directed integrations such as a DNS registrar, Microsoft, Google Cloud DNS, or an external storage provider process data under the customer’s own account and instructions. Their independent terms also apply.",
      },
      {
        id: "boundaries",
        title: "3. Processing boundaries",
        paragraphs: [
          "A provider receives only the categories reasonably needed for its stated purpose. Open-source software operated by Envelaro is not itself a subprocessor unless its operator receives customer personal data.",
          "Payment-card numbers are collected by the payment provider’s hosted payment surface and are not stored in Envelaro application databases.",
        ],
      },
      {
        id: "changes",
        title: "4. Changes",
        paragraphs: [
          "This list will be updated when a provider is added, replaced, or materially changes purpose. Contracted customers will receive any notice required by their final DPA.",
        ],
      },
    ],
  },
  {
    path: "/service-availability",
    title: "Service availability",
    shortTitle: "Service availability",
    summary:
      "How service availability, maintenance, incidents, support, and plan-specific commitments are handled.",
    status: "Effective",
    effectiveDate: updated,
    sections: [
      {
        id: "standard",
        title: "1. Standard service commitment",
        paragraphs: [
          "Envelaro operates the standard service using commercially reasonable care and aims to restore material interruptions promptly. Unless an order expressly includes a service-level agreement, no guaranteed uptime percentage, response time, or service credit applies.",
          "The public status page is the primary location for material platform incident updates. Support requests may be submitted through the customer portal or contact page.",
        ],
      },
      {
        id: "exclusions",
        title: "2. Availability exclusions",
        bullets: [
          "Planned or emergency maintenance communicated where reasonably practical.",
          "Customer configuration, DNS, devices, networks, third-party integrations, or unauthorised use outside Envelaro’s control.",
          "Internet or recipient-mail-system conditions, spam filtering, rejection, or delivery routing outside Envelaro’s control.",
          "Abuse mitigation, security response, legal restrictions, force majeure, and preview or beta features.",
        ],
      },
      {
        id: "preview",
        title: "3. Trials, evaluations, and preview features",
        paragraphs: [
          "Trial access uses the same published conduct rules but is provided for evaluation and may have lower usage limits. Preview and beta features may be changed, interrupted, reset, or withdrawn and must not be used as the only copy of important data.",
        ],
      },
      {
        id: "sla",
        title: "4. Contracted service levels",
        paragraphs: [
          "If an order includes a service-level agreement, that agreement identifies covered services, measurement, exclusions, support objectives, and any exclusive service-credit remedy. Marketing statements do not create an SLA.",
        ],
      },
    ],
  },
  {
    path: "/service-model",
    title: "Managed service model",
    shortTitle: "Service model",
    summary:
      "A plain-language description of what Envelaro sells, who may use it, and the infrastructure access it does not provide.",
    status: "Effective",
    effectiveDate: updated,
    highlights: [
      { label: "Product", value: "Managed business communications SaaS" },
      { label: "Customer access", value: "Named users and tenant administrators" },
      { label: "Infrastructure access", value: "No VPS, root, shell, or server control" },
    ],
    sections: [
      {
        id: "product",
        title: "1. What Envelaro provides",
        paragraphs: [
          "Envelaro provides a managed business communications service for organisations using domains they own or are authorised to administer. The service combines business-domain email, webmail, secure IMAP and SMTP client access, contacts, calendars, tasks, team communication, shared storage, and tenant administration according to the selected plan.",
          "Customers buy access to the application and its managed features. Envelaro operates the underlying service and remains responsible for infrastructure administration.",
        ],
      },
      {
        id: "not-hosting",
        title: "2. What Envelaro does not sell",
        bullets: [
          "No virtual private server, dedicated server, generic web-hosting account, cPanel account, root access, shell access, or operating-system control.",
          "No customer-controlled mail relay, open SMTP relay, proxy, bandwidth resale, IP-address rental, or infrastructure resale.",
          "No anonymous, disposable, consumer, or cryptocurrency-only mailbox product.",
          "No unsolicited bulk-email service, purchased-list delivery service, lead blasting, or evasion of another provider’s sending restrictions.",
        ],
      },
      {
        id: "verification",
        title: "3. Customer and domain verification",
        paragraphs: [
          "A customer administrator must use an identifiable business account and verify control of each sending domain through a unique DNS record before Envelaro activates managed mail routing. Envelaro may request business, administrator, beneficial-owner, domain, or intended-use evidence where risk or a payment provider requires it.",
          "Access is issued to named users inside the verified customer organisation. Tenant administrators manage domains, users, policies, and entitlements; mailbox users access only their assigned workspace and features.",
        ],
      },
      {
        id: "sending-controls",
        title: "4. Sending and abuse controls",
        bullets: [
          "Plan limits are enforced per mailbox: up to 400 external recipient deliveries per day on Starter, 600 on Growth, and 800 on Scale. Distribution-list recipients count individually.",
          "The card-free evaluation allowance is five external recipient deliveries per trial mailbox per day.",
          "Domain authentication guidance, rate controls, telemetry, complaint handling, and suspension tools protect customers, recipients, and shared reputation.",
          "The Acceptable Use Policy prohibits spam, phishing, malware, impersonation, compromised accounts, and attempts to bypass limits.",
        ],
      },
      {
        id: "review",
        title: "5. Product and processor review",
        paragraphs: [
          "Banks, payment processors, and enterprise reviewers may request a guided product demonstration or a time-limited review workspace through the contact page. Review access is named, monitored, non-transferable, and must not be used for production or unsolicited email.",
          "Sensitive corporate, ownership, banking, tax, and provider-contract evidence is supplied through the reviewer’s secure verification channel rather than published on this website.",
        ],
      },
    ],
  },
  {
    path: "/service-terms",
    title: "Service terms",
    shortTitle: "Service terms",
    summary:
      "The commercial terms for trials and paid Envelaro managed-service subscriptions.",
    status: "Effective",
    effectiveDate: updated,
    highlights: [
      { label: "Service", value: "Managed, organisation-based SaaS" },
      { label: "Billing", value: "Annual unless an order says otherwise" },
      { label: "Cancellation", value: "Any time before renewal" },
    ],
    sections: [
      {
        id: "agreement",
        title: "1. Agreement",
        paragraphs: [
          "These Service Terms are an agreement between the customer accepting an order and the Envelaro contracting party identified on that order, checkout page, or invoice. The person accepting confirms that they are authorised to bind the customer organisation.",
          "The order, these Service Terms, the Billing, Cancellation and Refund Policy, Acceptable Use Policy, Data Processing Addendum, and any signed addendum form the agreement. A signed order controls if it expressly states a conflicting term.",
        ],
      },
      {
        id: "service",
        title: "2. Service and access",
        paragraphs: [
          "Envelaro grants the customer a limited, non-exclusive, non-transferable right for its authorised users to use the managed service during the subscription term. Plans include only the capacities and features displayed at checkout or stated in the order.",
          "Envelaro is a managed application service, not a sale or lease of servers. Customers receive no VPS, root, shell, operating-system, control-panel, relay, or infrastructure-administration access.",
        ],
      },
      {
        id: "customer",
        title: "3. Customer responsibilities",
        bullets: [
          "Provide accurate account, business, tax, payment, administrator, and domain information and keep it current.",
          "Verify authority over every connected domain and obtain all rights and permissions needed for customer data and communications.",
          "Protect credentials, configure authorised users, remove access promptly, and notify Envelaro of suspected compromise or unauthorised use.",
          "Comply with the Acceptable Use Policy, applicable law, plan limits, and the rules of customer-selected third-party integrations.",
        ],
      },
      {
        id: "changes",
        title: "4. Service changes",
        paragraphs: [
          "Envelaro may improve or change features, interfaces, limits, and supporting providers. We will not materially reduce the core paid service during a current term without reasonable notice, except where a change is needed for security, law, abuse prevention, or a third-party dependency outside our control.",
        ],
      },
      {
        id: "fees",
        title: "5. Fees, renewal, and taxes",
        paragraphs: [
          "Fees, currency, billing period, included capacity, tax, and renewal timing are shown before purchase. Subscriptions renew automatically for the same billing period unless cancelled before renewal. The Billing, Cancellation and Refund Policy explains payment timing, cancellation, and refund eligibility.",
          "The customer is responsible for applicable taxes except taxes based on Envelaro’s net income. If payment fails, Envelaro may retry the authorised payment method and suspend paid features after reasonable notice.",
        ],
      },
      {
        id: "data",
        title: "6. Customer data",
        paragraphs: [
          "The customer retains its rights in customer data and instructs Envelaro to process it only as needed to provide, secure, support, and improve the contracted service, comply with law, and prevent abuse. The Data Processing Addendum governs personal data processed on the customer’s behalf.",
          "Customers remain responsible for lawful content and for retaining independent copies appropriate to their business. Export and deletion procedures are described in the Data Lifecycle and Export Policy.",
        ],
      },
      {
        id: "suspension",
        title: "7. Suspension and enforcement",
        paragraphs: [
          "Envelaro may restrict sending, quarantine content, or suspend affected access when reasonably necessary to address abuse, compromise, legal requirements, non-payment, material breach, or risk to recipients, customers, or infrastructure. Where circumstances permit, we will notify the customer and allow a reasonable opportunity to remedy the issue.",
        ],
      },
      {
        id: "term",
        title: "8. Term, cancellation, and termination",
        paragraphs: [
          "The agreement begins when the customer accepts an order or starts a trial. A customer may cancel renewal from the billing portal or through support; cancellation normally takes effect at the end of the paid term. Either party may terminate for an uncured material breach after reasonable notice, or immediately where law, fraud, serious abuse, or security risk requires it.",
          "After termination, access ends and the Data Lifecycle and Export Policy applies. Sections intended by their nature to survive, including payment obligations, confidentiality, intellectual property, disclaimers, liability limits, and dispute terms, remain effective.",
        ],
      },
      {
        id: "warranties",
        title: "9. Warranties and disclaimers",
        paragraphs: [
          "Envelaro warrants that it will provide the paid service with reasonable skill and care. Except for express commitments in the agreement and rights that cannot be excluded, the service is provided as available and Envelaro does not guarantee uninterrupted operation, recipient acceptance, inbox placement, or compatibility with every third-party system.",
        ],
      },
      {
        id: "liability",
        title: "10. Liability",
        paragraphs: [
          "To the extent permitted by law, neither party is liable for indirect, incidental, special, punitive, or consequential loss, or lost profits, revenue, goodwill, or anticipated savings. Each party’s aggregate liability arising from the service is limited to fees paid or payable for the affected service during the twelve months before the event giving rise to the claim.",
          "These limits do not apply to fraud, wilful misconduct, infringement by a party, payment obligations, or liability that cannot lawfully be limited. Mandatory customer or consumer rights remain unaffected.",
        ],
      },
      {
        id: "general",
        title: "11. General",
        paragraphs: [
          "The governing law and dispute forum are those identified in the accepted order or invoice. Neither party is liable for delay caused by events outside its reasonable control. Neither party may assign the agreement without the other’s consent, except to an affiliate or as part of a merger, reorganisation, financing, or sale of substantially all relevant assets.",
          "Notices may be delivered through the customer portal, the account email, or the contact details on the order. If a provision is unenforceable, it will be limited to the minimum extent necessary and the remainder continues in effect.",
        ],
      },
    ],
  },
  {
    path: "/billing-policy",
    title: "Billing, cancellation and refund policy",
    shortTitle: "Billing and refunds",
    summary:
      "Clear rules for trials, charges, renewals, cancellation, refunds, taxes, and statement descriptors.",
    status: "Effective",
    effectiveDate: updated,
    highlights: [
      { label: "Trial", value: "7 business days, no card required" },
      { label: "Renewal", value: "Cancel any time before renewal" },
      { label: "Initial refund", value: "Request within 14 calendar days" },
    ],
    sections: [
      {
        id: "trial",
        title: "1. Card-free trial",
        paragraphs: [
          "The standard trial lasts seven business days, requires no payment card, supports up to three mailboxes, and permits five external recipient deliveries per mailbox each day. The trial begins only when an administrator explicitly starts it after verifying a domain and configuring the first mailbox.",
          "A trial does not convert into a paid subscription automatically. The customer must choose a plan, review the total, and complete checkout to activate paid service.",
        ],
      },
      {
        id: "charges",
        title: "2. Charges and renewal",
        paragraphs: [
          "Self-service plans are billed annually in advance unless the accepted order states otherwise. The checkout page shows the plan, included capacity, currency, subtotal, tax, total due, renewal interval, and payment provider before the customer authorises payment.",
          "Paid access begins after successful payment or another written activation method. Subscriptions renew automatically at the then-current price unless cancelled before the renewal date. We will provide advance notice of a material price change where required by law.",
        ],
      },
      {
        id: "descriptor",
        title: "3. Receipt and statement descriptor",
        paragraphs: [
          "The checkout and receipt identify Envelaro and the payment provider. The card or bank statement should contain ENVELARO, sometimes together with the payment provider’s name or an abbreviated descriptor imposed by the financial institution. The exact descriptor is displayed at checkout or on the receipt before it is relied upon.",
          "Customers should contact support promptly if they do not recognise a charge. Envelaro will provide the associated order, invoice, and payment reference after appropriate account verification.",
        ],
      },
      {
        id: "cancellation",
        title: "4. Cancellation",
        paragraphs: [
          "Customers may cancel automatic renewal at any time from the authenticated billing portal or by submitting a verified support request. Cancellation normally takes effect at the end of the current paid term, and access remains available through that date unless the agreement is terminated earlier for another reason.",
          "Cancellation stops future renewal charges; it does not automatically erase data or create a refund. Customers should export needed data before the service end date. The Data Lifecycle and Export Policy explains the process.",
        ],
      },
      {
        id: "refunds",
        title: "5. Refund eligibility",
        bullets: [
          "Initial self-service purchase: a customer may request a full refund within 14 calendar days after the first paid activation.",
          "Automatic renewal: a customer may request a full refund within seven calendar days after renewal if there has been no material use of the service after that renewal.",
          "Billing error: duplicate, incorrect, or unauthorised charges confirmed by Envelaro will be corrected or refunded.",
          "Service failure: a refund or credit may be provided where required by the agreement or applicable law, including when Envelaro cannot provide the purchased service and no reasonable remedy is available.",
        ],
        note:
          "One-time migration, custom configuration, domain-registration, third-party pass-through, and other separately identified non-refundable costs are excluded once performed or committed, except where law requires otherwise. Approved refunds are returned to the original payment method; bank processing time is outside Envelaro’s control.",
      },
      {
        id: "requests",
        title: "6. Billing requests and disputes",
        paragraphs: [
          "A refund or billing request must identify the customer account, charge or invoice, reason, and a reply address controlled by an authorised administrator. Envelaro may request identity or account verification before discussing payment records.",
          "Please contact Envelaro before initiating a payment dispute so we can investigate quickly. This request does not waive any right to contact the payment provider or card issuer.",
        ],
      },
    ],
  },
  {
    path: "/data-lifecycle",
    title: "Data lifecycle and export policy",
    shortTitle: "Data lifecycle",
    summary:
      "How customers can retrieve data and what happens to service data after cancellation or termination.",
    status: "Effective",
    effectiveDate: updated,
    highlights: [
      { label: "Mailbox export", value: "Standards-based IMAP access" },
      { label: "Active data", value: "Delete within 30 days after service end" },
      { label: "Backup expiry", value: "Within 90 days" },
    ],
    sections: [
      {
        id: "during-service",
        title: "1. Access during service",
        paragraphs: [
          "Customers can access mailbox data during an active subscription through Envelaro webmail and standards-based secure IMAP. Where a product area provides a standard export or download function, customers may use it for contacts, calendar records, files, and administrative records.",
          "Tenant administrators may request a reasonable machine-readable export of available account and configuration data that is not otherwise self-service. Exports exclude Envelaro security methods, other customers’ data, internal risk signals, and material Envelaro is legally required to withhold.",
        ],
      },
      {
        id: "before-end",
        title: "2. Before cancellation takes effect",
        paragraphs: [
          "Cancellation is normally scheduled for the end of the paid term. The customer should complete mailbox synchronisation or export and download needed records before that service end date. Envelaro can provide reasonable procedural guidance but is not responsible for limitations in a customer’s selected export application.",
        ],
      },
      {
        id: "after-end",
        title: "3. After service ends",
        paragraphs: [
          "Envelaro disables ordinary user access when the service term ends. Unless law, a dispute, security investigation, or a signed order requires a different period, Envelaro deletes or irreversibly anonymises customer content from active systems within 30 days after the service end date.",
          "Encrypted backup copies may persist until the relevant backup expires or is overwritten, normally within 90 days after deletion from active systems. Backups are access-restricted and are not restored for ordinary product use after termination.",
        ],
      },
      {
        id: "early-deletion",
        title: "4. Early deletion and legal retention",
        paragraphs: [
          "An authorised administrator may request earlier deletion after termination where technically feasible. Envelaro may retain limited billing, consent, fraud-prevention, security, support, and legal records for the period required by law or reasonably needed to establish and defend rights.",
          "Suspension does not itself delete data. If service is terminated for serious abuse or legal reasons, Envelaro may preserve relevant evidence and may be unable to provide content that would create further harm or violate law.",
        ],
      },
      {
        id: "assistance",
        title: "5. Assisted export",
        paragraphs: [
          "If a customer cannot use the available self-service methods, an authorised administrator may request assisted export before the service end date. Envelaro will confirm scope, identity checks, secure delivery method, expected timing, and any reasonable fee for exceptional engineering or media work before proceeding.",
        ],
      },
    ],
  },
];

export const legalDocumentByPath = new Map(
  legalDocuments.map((document) => [document.path, document]),
);
